// Descrizione di un blocco (dettaglio) ricavata dalle sue andature, in testo semplice o HTML.
//
// - plain: testo delle note del blocco. Se le note sono vuote, si mostra all'atleta
//   (desktop e mobile); il tasto "compila descrizione" della scheda allenatore
//   (mixins/dettaglio.js) lo scrive nelle note. Gemello PHP: descrizioneAutomaticaDettaglio()
//   in allenamento_calcoli.php (stampa, email dell'allenamento del giorno): stringhe e
//   struttura vanno tenute uguali.
// - html: box andature del mobile (MspAndatureDettaglio.vue). Può mostrare anche i
//   valori della zona ricavati dal test dell'atleta (opzione valoriZona).
// - parti (partiAndatura, ripetuteBlocco, ripetuteSerie): stesso contenuto senza markup,
//   per i template che vogliono decidere da sé elementi e classi (MspAndatureDettaglio.vue
//   desktop). Plain e html sono costruiti sulle stesse parti.
import ums from '@cached/um.json';
import azr from '@src/js/vue/mixins/andaturaZonaRange.js';
import { formatText, configAndaturaSport } from '@src/js/lib/configAndatura.js';
import _funzioni from '@shared/msp/funzioni.js';
import MSPFormatter from '@shared/msp/formatter.js';
import { getConfig } from '@shared/config/runtime.js';

const space = "    ";

// Le zone del nuoto (A1, B2, ...) non hanno la "Z" e non hanno range.
const isZonaNuoto = function (andatura) {
    return andatura.des_zona && andatura.des_zona.indexOf('Z') === -1;
};

const valoreAndatura = function (andatura) {
    if (!andatura.val_andatura) return "";
    const um = ums.find(el=>+el.id===+andatura.um_id);
    if (!um) return "";
    if (um.um_tipo_id === 1) {
        return MSPFormatter.sec2hms_auto(+andatura.val_andatura);
    }
    return `${andatura.val_andatura} ${um.suffix}`;
};

// Nome del range (bassa/centrale/alta) seguito dai valori della zona, se disponibili.
const rangeAndatura = function (andatura, valoriZona) {
    if (andatura.des_zona === "REC") return "";
    const parti = [];
    if (!isZonaNuoto(andatura)) {
        parti.push(azr.zonaRangeDescrizione(andatura, ""));
    }
    if (valoriZona) {
        parti.push(valoriZona(andatura) || "");
    }
    return parti.filter(el => el && el.trim().length).join(" ");
};

/**
 * Parti di una singola andatura, senza markup: decide cosa si mostra e in che ordine,
 * la presentazione è lasciata a chi le usa (formatter plain/html qui sotto, oppure il
 * template del componente, che può dare a ogni parte le sue classi).
 * Gemello PHP: formatAndatura() + opzioni in descrizioneAutomaticaDettaglio().
 *
 * Tipi, nell'ordine in cui compaiono:
 * - valore: {testo} es. "10:00", "2 km"
 * - recupero | zona: {testo} "rec. fermo" oppure il nome della zona ("Z2", "A1")
 * - range: {testo} nome del range e valori della zona, mai sul recupero
 * - opzioni: {voci: [{key, testo, html, newLine}]} opzioni dell'andatura (config dello
 *   sport); html è il formatter della config, che può contenere markup (es. <span class='um'>);
 *   newLine (view.newLine della config) se l'opzione va su una nuova riga
 *
 * @param {Object} andatura
 * @param {number} sport_id
 * @param {Object} [opzioni]
 * @param {Function} [opzioni.valoriZona] (andatura) => stringa con i valori della zona
 *   ricavati dal test dell'atleta (es. "92 - 104 W"); senza, si mostra solo il nome del range.
 * @return {Array<Object>} [] se l'andatura non ha unità di misura
 */
const partiAndatura = function (andatura, sport_id, { valoriZona = null } = {}) {
    if (!andatura.um_id) return [];
    const parti = [];

    const valore = valoreAndatura(andatura);
    if (valore) parti.push({ tipo: 'valore', testo: valore });

    if (andatura.des_zona === "REC") {
        parti.push({ tipo: 'recupero', testo: 'rec. fermo' });
    } else if (andatura.des_zona) {
        parti.push({ tipo: 'zona', testo: andatura.des_zona });
    }

    const range = rangeAndatura(andatura, valoriZona);
    if (range) parti.push({ tipo: 'range', testo: range });

    const voci = configAndaturaSport(sport_id)
        .map(field => ({
            key: field.id,
            testo: formatText(field, andatura, true) || "",
            html: formatText(field, andatura, false) || "",
            newLine: !!field.view.newLine,
        }))
        .filter(el => el.testo.length);
    if (voci.length) parti.push({ tipo: 'opzioni', voci });

    return parti;
};

// Opzioni in testo semplice: separate da " | ", su una nuova riga quelle con newLine.
// Gemello PHP: testoOpzioniPlain() in allenamento_calcoli.php.
const testoOpzioniPlain = function (voci) {
    return voci.reduce((acc, voce, i) => {
        if (voce.newLine) return acc + "\n" + voce.testo;
        return acc + (i ? " | " : " ") + voce.testo;
    }, "");
};

const formatAndaturaPlain = function (parti) {
    let result = "";
    parti.forEach((parte) => {
        switch (parte.tipo) {
            case 'valore': result += parte.testo; break;
            case 'recupero': result += " " + parte.testo; break;
            case 'zona': result += " in " + parte.testo; break;
            case 'range': result = (result + " " + parte.testo).trim(); break;
            case 'opzioni': result = result.trim() + testoOpzioniPlain(parte.voci); break;
        }
    });
    return result.trim();
};

// Classi di un'opzione nel markup: option--{id} per lo stile della singola opzione,
// option--new-line (view.newLine) per mandarla su una nuova riga.
const classiOpzione = function (voce) {
    const classi = ["option", "option--" + voce.key];
    if (voce.newLine) classi.push("option--new-line");
    return classi;
};

// Markup del box andature del mobile (classi CSS di MspAndatureDettaglio.vue).
const formatAndaturaHtml = function (parti) {
    return parti.map((parte) => {
        switch (parte.tipo) {
            case 'valore': return " " + parte.testo;
            case 'recupero': return `<span class='recupero'> ${parte.testo}</span>`;
            // &nbsp; tra "in" e la zona: niente a capo fra i due quando .andatura-value va a capo.
            case 'zona': return ` in&nbsp;<strong class='zona'>${parte.testo}</strong>`;
            case 'range': return `<span class='range'> ${parte.testo}</span>`;
            case 'opzioni': {
                const spans = parte.voci.map(el => `<span class="${classiOpzione(el).join(" ")}">${el.html}</span>`);
                return `<span class='options flex gap-2'>${spans.join("")}</span>`;
            }
        }
        return "";
    }).join("");
};

/**
 * Riga di una singola andatura, v. partiAndatura.
 *
 * @param {Object} andatura
 * @param {number} sport_id
 * @param {Object} [opzioni] {format: 'plain'|'html', valoriZona}
 * @return {string}
 */
const renderAndatura = function (andatura, sport_id, { format = 'plain', valoriZona = null } = {}) {
    const parti = partiAndatura(andatura, sport_id, { valoriZona });
    return (format === 'html') ? formatAndaturaHtml(parti) : formatAndaturaPlain(parti);
};

// Ripetizioni del blocco da mostrare ("Ripeti blocco N volte"): 0 se il blocco si fa una volta sola.
const ripetuteBlocco = function (dettaglio) {
    const n = +dettaglio.ripetute;
    return (n > 1) ? n : 0;
};

// Ripetizioni della serie ("N x"), sempre scritte (anche 1 x).
const ripetuteSerie = function (serie) {
    return +serie.ripetute || 1;
};

const renderRipetiBlocco = function (dettaglio, { format = 'plain' } = {}) {
    const n = ripetuteBlocco(dettaglio);
    if (!n) return "";
    if (format === 'html') {
        return `Ripeti blocco <span class="ripetuta-dettaglio__numero">${n}</span>&nbsp;volte`;
    }
    return `Ripeti blocco ${n} volte`;
};

const renderRipetiSerie = function (serie, { format = 'plain' } = {}) {
    const n = ripetuteSerie(serie);
    return (format === 'html') ? `${n}&nbsp;x&nbsp;` : `${n} x`;
};

const hasAndatureConValore = function (dettaglio) {
    return dettaglio.serie.some(serie => (serie.andature || []).some(andatura => +andatura.val_andatura));
};

const renderSerieHtml = function (serie, sport_id, opzioni) {
    const andature = serie.andature || [];
    const multipla = +serie.ripetute > 1;
    const items = andature.map((andatura, i) => {
        const last = (i + 1 === andature.length) ? " last" : "";
        const plus = i ? `<span class="plus">+</span>` : "";
        return `<span class="andatura${last}">${plus}<span class="andatura-value">${renderAndatura(andatura, sport_id, opzioni)}</span></span>`;
    }).join("");
    return `<div class="serie"><span class="ripetuta-serie ripetuta">${renderRipetiSerie(serie, opzioni)}</span>`
        + `<span class="andature-container">${multipla ? '<span class="prefix">(</span>' : ''}${items}${multipla ? '<span class="suffix">)</span>' : ''}</span></div>`;
};

/**
 * Descrizione completa di un blocco.
 *
 * plain: "" se lo sport non ha zone o se nessuna andatura del blocco ha un valore (il
 * tasto "compila descrizione" non fa niente, l'atleta non vede note). Altrimenti tutte
 * le andature, anche quelle con valore 0.
 * html: markup di MspAndatureDettaglio (ripetizioni del blocco + serie), stesse classi CSS.
 *
 * @param {Object} dettaglio
 * @param {number} sport_id
 * @param {Object} [opzioni] {format: 'plain'|'html', valoriZona}, v. renderAndatura
 * @return {string}
 */
const renderDescrizione = function (dettaglio, sport_id, opzioni = {}) {
    if (!(dettaglio && dettaglio.serie && dettaglio.serie.length)) return "";
    const format = opzioni.format || 'plain';

    if (format === 'html') {
        const ripeti = renderRipetiBlocco(dettaglio, opzioni);
        return (ripeti ? `<div class="ripetuta-dettaglio ripetuta">${ripeti}</div>` : "")
            + `<div class="serie-container">${dettaglio.serie.map(serie => renderSerieHtml(serie, sport_id, opzioni)).join("")}</div>`;
    }

    if (!_funzioni.hasZone(sport_id)) return "";
    if (!hasAndatureConValore(dettaglio)) return "";
    const results = [];
    const ripeti = renderRipetiBlocco(dettaglio, opzioni);
    if (ripeti) results.push(ripeti);
    dettaglio.serie.forEach((serie) => {
        const righe = (serie.andature || [])
            .map(andatura => renderAndatura(andatura, sport_id, opzioni))
            .filter(riga => riga.length);
        if (!righe.length) return;
        results.push(renderRipetiSerie(serie, opzioni));
        righe.forEach(riga => results.push(space + riga));
    });
    return results.join("\n");
};

// Testo da mostrare come note del blocco: le note scritte, altrimenti quelle automatiche.
// Per un allenamento con descrizione automatica (automatica = isDescrizioneAutomatica())
// si mostra solo il testo automatico, anche se il blocco ha ancora note scritte.
const noteVisualizzate = function (dettaglio, sport_id, automatica = false) {
    if (!dettaglio) return "";
    if (automatica) return renderDescrizione(dettaglio, sport_id);
    const note = dettaglio.note_attivita;
    if (note && note.trim().length) return note;
    return renderDescrizione(dettaglio, sport_id);
};

// Funzione "Descrizione automatica" attiva (costante DESCRIZIONE_AUTOMATICA in constants.php,
// esposta in CP_CONFIG). Spenta: niente switch nella scheda allenatore, ogni allenamento è
// trattato come manuale e, senza note scritte, il blocco mostra il testo plain delle andature.
const isDescrizioneAutomaticaAttiva = function () {
    return !!getConfig().DESCRIZIONE_AUTOMATICA;
};

// Descrizione automatica/manuale dei blocchi: proprietà dell'allenamento
// (descrizione_automatica, 0/1 dal DB, assente = manuale). Un allenamento automatico non
// ha note nei blocchi: la scheda allenatore le svuota quando si passa ad automatico.
const isDescrizioneAutomatica = function (allenamento) {
    if (!isDescrizioneAutomaticaAttiva()) return false;
    return !!(allenamento && +allenamento.descrizione_automatica === 1);
};

// Ultima scelta automatica/manuale per sport, usata come valore iniziale di un nuovo
// allenamento (bozza di calendario e libreria).
const CHIAVE_PREFERENZA = 'schedaDescrizioneAutomatica';

const leggiPreferenze = function () {
    try {
        const preferenze = JSON.parse(localStorage.getItem(CHIAVE_PREFERENZA));
        return (preferenze && typeof preferenze === 'object') ? preferenze : {};
    } catch (e) {
        return {};
    }
};

const leggiPreferenzaDescrizioneAutomatica = function (sport_id) {
    if (!isDescrizioneAutomaticaAttiva()) return false;
    return !!leggiPreferenze()[+sport_id];
};

const salvaPreferenzaDescrizioneAutomatica = function (sport_id, value) {
    if (!sport_id) return;
    const preferenze = leggiPreferenze();
    preferenze[+sport_id] = !!value;
    try {
        localStorage.setItem(CHIAVE_PREFERENZA, JSON.stringify(preferenze));
    } catch (e) {
        // storage pieno o non disponibile: si perde solo la preferenza
    }
};

export {
    renderDescrizione,
    partiAndatura,
    classiOpzione,
    ripetuteBlocco,
    ripetuteSerie,
    noteVisualizzate,
    isDescrizioneAutomaticaAttiva,
    isDescrizioneAutomatica,
    leggiPreferenzaDescrizioneAutomatica,
    salvaPreferenzaDescrizioneAutomatica,
};
